import { Box, Grid, Tab, Tabs, useMediaQuery } from '@mui/material'
import { Typography } from '@evocolabs/orkaui'
import React, { createContext, useEffect, useMemo, useState } from 'react'
import { styled, useTheme } from '@mui/material/styles'

import { NextSeo } from 'next-seo'
import PropTypes from 'prop-types'
import SwipeableViews from 'react-swipeable-views'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import Header from '@/src/components/HeaderV2'
import AccountRouter from '@/src/components/Account/AccountRouter'
import AccountRouterMobile from '@/src/components/Account/AccountRouterMobile'
import AccountSideBar from '@/src/components/Account/AccountSideBar'
import Footer from '@/src/components/Footer/Footer2'
import NormalLayout from '@/src/components/NormalLayout'
import SpinningCircle from '@/src/components/SpinningCircle'
import { useBackend } from '@/src/providers/BackendProvider'

import { selector } from '@/src/redux/store'
import { IS_LOGIN, UUID } from '@/src/redux/state'

import RequireLogin from '@/src/utils/RequireLogin'

import { getAllKYCResults } from '@/src/providers/RemotePoint'

const usePrefittingStat = () => {
  const uid = useSelector(selector.selectUserInfo)[UUID]
  const userInfo = useSelector(selector.selectUserInfo)
  const [isKYCSubmitDone, setIsKYCSubmitDone] = useState(false)
  const { signedin } = useBackend()
  useEffect(() => {
    const fetchKYCSubmitResult = async () => {
      if (userInfo[IS_LOGIN]) {
        const { data } = await getAllKYCResults(uid)
        const isKYCSubmitDone = !!data?.[0]?.id
        setIsKYCSubmitDone(isKYCSubmitDone)
      }
    }
    fetchKYCSubmitResult()
  }, [signedin])
  return isKYCSubmitDone
}

const PREFIX = 'Account'

const classes = {
  typo: `${PREFIX}-typo`,
  circle: `${PREFIX}-circle`,
  appBar: `${PREFIX}-appBar`
}

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(() => ({
  [`& .${classes.typo}`]: {
    color: 'blue',
    textDecoration: 'line-through'
  },

  [`& .${classes.circle}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },

  [`& .${classes.appBar}`]: {
    '& .MuiAppBar-root': {
      backgroundColor: '#ffffff'
    }
  }
}))

const AccountMobile = ({
  currentView,
  setCurrentView,
  headerHeight,
  ...props
}) => {
  const { userDetails, orderDetails, loading, getAllPersonalData } =
    useBackend()
  const res = useMemo(() => {
    if (!userDetails || !orderDetails?.results || loading) {
      return (
        <Box
          bgcolor="#F7F3EB"
          width="100%"
          minHeight="100vh"
          pt="72px"
          pb={4}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <NormalLayout width="100%" height="100%">
            <Grid container spacing={2} style={{ justifyContent: 'center' }}>
              <Grid item sm={9}>
                <>
                  <SpinningCircle />
                </>
              </Grid>
            </Grid>
          </NormalLayout>
        </Box>
      )
    } else {
      const allPersonalData = getAllPersonalData(userDetails, orderDetails)
      const info = {
        firstName: userDetails.first_name,
        lastName: userDetails.last_name,
        year: userDetails.birth_year,
        gender: userDetails.gender,
        state: userDetails.state,
        email: userDetails.username,
        address: allPersonalData.shipping_addresses
      }
      return (
        <Box
          bgcolor="#F7F3EB"
          width="100%"
          minHeight="100vh"
          pb={4}
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="center"
        >
          <AccountHeader
            currentView={currentView}
            setCurrentView={setCurrentView}
            info={info}
            prefittingStat={props.prefittingStat}
            headerHeight={headerHeight}
          />
        </Box>
      )
    }
  }, [userDetails, orderDetails, currentView, loading, headerHeight])

  return <>{res}</>
}

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
}

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`
  }
}

const AccountHeader = ({
  currentView,
  setCurrentView,
  info,
  headerHeight,
  ...props
}) => {
  const theme = useTheme()
  const useMobile = useMediaQuery(theme.breakpoints.down('sm'))
  useEffect(() => {
    if (currentView === 'orders') {
      setValue(2)
    } else if (currentView === 'service') {
      setValue(1)
    }
  }, [currentView])
  // get current anchor and set initial <Tab />
  const getStateAccordingToAnchor = () => {
    let initialState = 0
    if (window.location.hash === '') initialState = 0
    else if (window.location.hash === '#service') initialState = 1
    else if (window.location.hash === '#orders') initialState = 2
    return initialState
  }
  const [value, setValue] = useState(getStateAccordingToAnchor())
  window.addEventListener('hashchange', () =>
    setValue(getStateAccordingToAnchor())
  )

  const handleChange = (event, newValue) => {
    if (newValue === 0) history.replaceState(null, null, ' ')
    else if (newValue === 1) window.location.hash = '#service'
    else if (newValue === 2) window.location.hash = '#orders'
    setValue(newValue)
  }

  const handleChangeIndex = (index) => {
    setValue(index)
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%'
      }}
    >
      <Box
        color="white"
        style={{
          boxShadow: 'none',
          position: 'sticky',
          width: '100%',
          top: `${headerHeight}px`,
          backgroundColor: '#fff',
          padding: '0px 16px',
          zIndex: 400
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          textColor="#1a1a1a"
          variant={useMobile ? 'scrollable' : 'fullWidth'}
          // variant='scrollable'
          scrollButtons={false}
          style={{ margin: 'auto', width: useMobile ? '' : '100%' }}
          TabIndicatorProps={{
            sx: {
              backgroundColor: theme.palette.primary[600],
              height: '2px'
            }
          }}
        >
          <Tab
            id="account_account_details"
            label={<Typography variant="Body2">Account Details</Typography>}
            sx={{
              textTransform: 'none',
              color: '#26201F'
            }}
            {...a11yProps(0)}
          />
          <Tab
            id="account_service_center"
            label={<Typography variant="Body2">Service Center</Typography>}
            sx={{
              textTransform: 'none',
              color: '#26201F'
            }}
            {...a11yProps(1)}
          />
          <Tab
            id="account_my_orders"
            label={<Typography variant="Body2">My Orders</Typography>}
            sx={{
              textTransform: 'none',
              color: '#26201F'
            }}
            {...a11yProps(2)}
          />
        </Tabs>
      </Box>
      <SwipeableViews
        axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
        index={value}
        onChangeIndex={handleChangeIndex}
        slideStyle={{ position: 'relative' }}
      >
        <TabPanel value={value} index={0} dir={theme.direction}>
          <AccountRouterMobile
            info={info}
            currentView="detail"
            setCurrentView={setCurrentView}
            setValue={setValue}
            prefittingStat={props.prefittingStat}
            isKYCSubmitDone={props.isKYCSubmitDone}
          />
        </TabPanel>
        <TabPanel value={value} index={1} dir={theme.direction}>
          <AccountRouterMobile
            info={info}
            currentView="service"
            setCurrentView={setCurrentView}
            setValue={setValue}
            prefittingStat={props.prefittingStat}
            isKYCSubmitDone={props.isKYCSubmitDone}
          />
        </TabPanel>
        <TabPanel value={value} index={2} dir={theme.direction}>
          <AccountRouterMobile
            info={info}
            currentView="orders"
            setCurrentView={setCurrentView}
            setValue={setValue}
            prefittingStat={props.prefittingStat}
            isKYCSubmitDone={props.isKYCSubmitDone}
          />
        </TabPanel>
      </SwipeableViews>
    </div>
  )
}

const AccountPC = ({
  currentView,
  setCurrentView,
  isKYCSubmitDone,
  ...props
}) => {
  const { userDetails, orderDetails, loading, getAllPersonalData } =
    useBackend()
  const res = useMemo(() => {
    if (!userDetails || !orderDetails?.results || loading) {
      return (
        <Box
          bgcolor="#F7F3EB"
          width="100%"
          minHeight="100vh"
          pt={4}
          pb={4}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <NormalLayout bgcolor="transparent" width="100%" height="100%">
            <Grid container spacing={2} style={{ justifyContent: 'center' }}>
              <Grid item sm={9} className={classes.circle}>
                <SpinningCircle />
              </Grid>
            </Grid>
          </NormalLayout>
        </Box>
      )
    } else {
      const allPersonalData = getAllPersonalData(userDetails, orderDetails)
      const info = {
        firstName: userDetails.first_name,
        lastName: userDetails.last_name,
        year: userDetails.birth_year,
        gender: userDetails.gender,
        state: userDetails.state,
        email: userDetails.username,
        address: allPersonalData.shipping_addresses
      }

      return (
        <Box
          bgcolor="#F7F3EB"
          width="100%"
          minHeight="100vh"
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="center"
          pt={4}
          paddingBottom="64px"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              maxWidth: '1320px'
            }}
          >
            <AccountSideBar
              info={info}
              setCurrentView={setCurrentView}
              currentView={currentView}
              prefittingStat={isKYCSubmitDone || props.prefittingStat}
            />
            <AccountRouter
              info={info}
              currentView={currentView}
              prefittingStat={props.prefittingStat}
              isKYCSubmitDone={isKYCSubmitDone}
            />
          </Box>
        </Box>
      )
    }
  }, [userDetails, orderDetails, loading, currentView])
  return <>{res}</>
}

const Account = () => {
  const theme = useTheme()
  const [currentView, setCurrentView] = useState('detail')
  const [orderState, setOrderState] = useState(0)
  const [prefittingStat, setPrefittingStat] = useState(false)
  const { signedin, getPrefittingStatus } = useBackend()
  const router = useRouter()
  const usePC = useMediaQuery(theme.breakpoints.up('md'))
  const userInfo = useSelector(selector.selectUserInfo)
  const isKYCSubmitDone = usePrefittingStat()

  const headerHeight = useSelector((state) => state.header.height)
  useEffect(() => {
    ;(async () => {
      if (userInfo[IS_LOGIN]) {
        const res = await getPrefittingStatus(userInfo[UUID])
        // server returned a JSON string with single quote
        const parsed = JSON.parse(
          res.data.survey_result_items.replace(/'/g, '"')
        )
        // Tried to preserve the previous state (While the previous state is always undefined)
        setPrefittingStat(parsed[0])
      }
    })()
  }, [signedin])

  useEffect(() => {
    const section = router.asPath.split('#')[1]
    if (section && (section === 'orders' || section === 'service')) {
      setCurrentView(section)
    } else {
      setCurrentView('detail')
    }
  }, [router])

  return (
    <RequireLogin>
      <Root>
        <NextSeo
          title="Account | Orka - Hear what matters"
          description="Welcome back! Log in to your Orka account and check our updates about your Orka hearing aids! If there are any issues with your account, please contact us."
        />
        <OrderState.Provider value={{ orderState, setOrderState }}>
          <Header isWhiteBg />
          {usePC ? (
            <AccountPC
              currentView={currentView}
              setCurrentView={setCurrentView}
              prefittingStat={prefittingStat}
              isKYCSubmitDone={isKYCSubmitDone}
            />
          ) : (
            <AccountMobile
              currentView={currentView}
              setCurrentView={setCurrentView}
              prefittingStat={prefittingStat}
              isKYCSubmitDone={isKYCSubmitDone}
              headerHeight={headerHeight}
            />
          )}
        </OrderState.Provider>
        <Footer miniMode={true} bgcolor={'#F7F3EB'} />
      </Root>
    </RequireLogin>
  )
}

export const OrderState = createContext()
export default Account
